import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  inject,
} from '@angular/core';
import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import type { RequestError } from '@freelancer/datastore';
import type { BackendErrorResponse } from '@freelancer/freelancer-http';
import { ErrorTracking } from '@freelancer/tracking';
import { TrackingModule } from '@freelancer/tracking';
import { textVariants } from '@freelancer/ui/tailwind-text';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import { exceptionErrors, GenericErrorAlign } from './generic-error.types';
import { LinkModule } from '../link/link.module';

@Component({
  standalone: true,
  imports: [LinkModule, NgTemplateOutlet, TrackingModule],
  selector: 'fl-generic-error',
  template: `
    <div
      class="ErrorContainer"
      [attr.data-align]="align"
    >
      <!-- Error message title -->
      @if (multiLine) {
        <p
          [class]="cls"
          [class.mb-xxsmall]="true"
        >
          <ng-container *ngTemplateOutlet="errorTitleContent"></ng-container>
        </p>
      }
      @if (!multiLine) {
        <span
          [class]="cls"
          [class.mb-none]="true"
        >
          <ng-container *ngTemplateOutlet="errorTitleContent"></ng-container>
        </span>
      }

      <!-- Error solution suggestions -->
      @if (multiLine) {
        <p
          [class]="cls"
          [class.mb-xxsmall]="true"
        >
          <ng-container *ngTemplateOutlet="errorSolutionContent"></ng-container>
        </p>
      }
      @if (!multiLine) {
        <span
          [class]="cls"
          [class.mb-none]="true"
        >
          <ng-container *ngTemplateOutlet="errorSolutionContent"></ng-container>
        </span>
      }

      <!-- Support suggestion -->
      @if (multiLine) {
        <p
          [class]="cls"
          [class.mb-xxsmall]="true"
        >
          <ng-container
            *ngTemplateOutlet="supportSuggestionContent"
          ></ng-container>
        </p>
      }
      @if (!multiLine) {
        <span
          [class]="cls"
          [class.mb-none]="true"
        >
          <ng-container
            *ngTemplateOutlet="supportSuggestionContent"
          ></ng-container>
        </span>
      }
    </div>
    <div
      class="ErrorContainer"
      [attr.data-align]="align"
    >
      <!-- Error code -->
      @if (multiLine) {
        <p [class]="cls">
          <ng-container *ngTemplateOutlet="errorCodeContent"></ng-container>
        </p>
      }
      @if (!multiLine) {
        <span [class]="cls">
          <ng-container *ngTemplateOutlet="errorCodeContent"></ng-container>
        </span>
      }

      <!-- Request ID -->
      @if (multiLine && error.requestId) {
        <p [class]="cls">
          <ng-container *ngTemplateOutlet="requestIdContent"></ng-container>
        </p>
      }
      @if (!multiLine && error.requestId) {
        <span [class]="cls">
          <ng-container *ngTemplateOutlet="requestIdContent"></ng-container>
        </span>
      }
    </div>

    <!-- Content templates -->
    <ng-template
      #errorTitleContent
      i18n="Generic error message title"
    >
      Oops, something went wrong.
      <!-- supplied message, eg. "could not place your bid" -->
      <ng-content></ng-content>
      @if (isRateLimit) {
        You've sent too many requests.
      }
      @if (isNetworkError) {
        There seems to be a connection issue.
      }
      @if (isServerError) {
        There seems to be a server issue.
      }
    </ng-template>

    <ng-template
      #errorSolutionContent
      i18n="Generic error solution suggestions text"
    >
      Please
      @if (canRetry || hasSetRetry) {
        <fl-link
          flTrackingLabel="LinkToRetryGenericError"
          (click)="handleRetry($event)"
        >
          try again.
        </fl-link>
      } @else {
        @if (isRateLimit) {
          wait and try again later.
        } @else {
          @if (isNetworkError) {
            check your connection and try again.
          } @else {
            try again.
          }
        }
      }
    </ng-template>

    <ng-template
      #supportSuggestionContent
      i18n="Default redirect to support suggestion"
    >
      If the problem persists, please contact
      <fl-link
        flTrackingLabel="LinkToSupportGenericError"
        [link]="'/support'"
      >
        support
      </fl-link>
      with the following error information:
    </ng-template>

    <ng-template
      #errorCodeContent
      i18n="Generic error message error code"
    >
      Error Code: {{ error.errorCode }}
    </ng-template>

    <ng-template
      #requestIdContent
      i18n="Generic error message request ID"
    >
      Request ID: {{ error.requestId }}
    </ng-template>
  `,
  styleUrls: ['./generic-error.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenericErrorComponent implements OnInit, OnChanges {
  private errorTracking = inject(ErrorTracking);

  @HostBinding('attr.ngSkipHydration') skipHydration = 'true';

  isRateLimit = false;
  isNetworkError = false;
  isServerError = false;

  canRetry = false;
  hasSetRetry = false;

  hasGenericErrorCode = false;

  cls: string;

  @Input() align = GenericErrorAlign.LEFT;
  @Input() error: BackendErrorResponse<any> | RequestError<any>;
  @Input() multiLine = false;
  @Input() fontColor = FontColor.FOREGROUND;

  @Output() retry = new EventEmitter<MouseEvent>();

  ngOnInit(): void {
    this.computeClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.isRateLimit = this.error.errorCode === ErrorCodeApi.TOO_MANY_REQUESTS;
    this.isNetworkError = this.error.errorCode === 'NETWORK_ERROR';
    this.isServerError = this.error.errorCode === 'SERVER_ERROR';
    this.canRetry =
      ('retry' in this.error && !this.isRateLimit) ||
      this.isNetworkError ||
      this.isServerError;
    this.hasSetRetry =
      this.retry.observed &&
      !this.isRateLimit &&
      !this.isNetworkError &&
      !this.isServerError;
    this.hasGenericErrorCode = exceptionErrors.includes(this.error.errorCode);

    if (!this.hasGenericErrorCode) {
      this.errorTracking.captureMessage(
        `fl-generic-error has non generic error: ${this.error.errorCode}`,
        {
          extra: {
            request_id: this.error.requestId,
          },
        },
      );
    }

    if ('fontColor' in changes) {
      this.computeClasses();
    }
  }

  handleRetry(e: MouseEvent): void {
    if (this.hasSetRetry) {
      this.retry.emit(e);
    } else if (this.canRetry) {
      (this.error as RequestError<any>).retry();
    }
  }

  private computeClasses(): void {
    this.cls = textVariants({
      color: this.fontColor,
      weight: FontWeight.NORMAL,
      size: TextSize.XSMALL,
    });
  }
}
